
import React from 'react';
import { Task, TaskType } from '../types';
import { AlertCircle, Clock, Link as LinkIcon, BoxSelect } from 'lucide-react';

interface GanttChartProps {
  tasks: Task[];
}

const GanttChart: React.FC<GanttChartProps> = ({ tasks }) => {
  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500 border-2 border-dashed border-slate-700 rounded-xl">
        <Clock className="w-12 h-12 mb-4 opacity-50" />
        <p>No tasks defined yet. Ask the AI Architect to build your plan.</p>
      </div>
    );
  }

  // Calculate project duration in HOURS
  const projectDurationHours = Math.max(...tasks.map(t => t.earlyFinish || 0));
  // Scale: Pixels per HOUR
  const pixelsPerHour = 30; 
  const totalWidth = Math.max(800, projectDurationHours * pixelsPerHour + 100);

  return (
    <div className="w-full overflow-x-auto bg-slate-900 rounded-xl border border-slate-800 shadow-xl">
      <div className="p-4 border-b border-slate-800 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Critical Path Visualization (Hourly Scale)</h3>
        <div className="flex gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-red-500 rounded-sm"></span>
            <span className="text-slate-300">Critical</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-600 rounded-sm"></span>
            <span className="text-slate-300">Standard</span>
          </div>
           <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-slate-800 border border-slate-500 border-dashed rounded-sm"></span>
            <span className="text-slate-300">Placeholder</span>
          </div>
        </div>
      </div>

      <div className="relative p-6" style={{ minWidth: `${totalWidth}px` }}>
        {/* Time Axis */}
        <div className="flex border-b border-slate-700 mb-4 pb-2 sticky left-0 z-10">
            <div className="w-48 shrink-0 text-slate-400 font-medium text-xs uppercase tracking-wider">Task / Session</div>
            <div className="flex-1 relative h-6">
              {/* Markers every 4 hours */}
              {Array.from({ length: Math.ceil(projectDurationHours / 4) + 2 }).map((_, i) => (
                <div 
                  key={i} 
                  className="absolute text-[10px] text-slate-500 border-l border-slate-800 h-full pl-1"
                  style={{ left: `${i * 4 * pixelsPerHour}px` }}
                >
                  H{i * 4}
                </div>
              ))}
            </div>
        </div>

        {/* Tasks */}
        <div className="space-y-3">
          {tasks.map((task) => {
            const width = Math.max(20, (task.earlyFinish! - task.earlyStart!) * pixelsPerHour);
            const left = (task.earlyStart || 0) * pixelsPerHour;
            const floatWidth = (task.slack || 0) * pixelsPerHour;
            const isCritical = task.isCritical;
            const isPlaceholder = task.type === TaskType.PLACEHOLDER;

            return (
              <div key={task.id} className="flex group hover:bg-slate-800/30 rounded py-1 transition-colors">
                <div className="w-48 shrink-0 pr-4 flex items-center justify-between text-sm text-slate-300">
                  <span className="truncate" title={task.name}>{task.name}</span>
                  {isCritical && <AlertCircle className="w-3 h-3 text-red-500" />}
                  {isPlaceholder && <BoxSelect className="w-3 h-3 text-slate-500" />}
                </div>
                
                <div className="flex-1 relative h-8">
                  {/* Slack Bar (Ghost) */}
                  {floatWidth > 0 && !isCritical && (
                     <div
                     className="absolute top-1 h-6 bg-slate-700/20 border border-slate-700 border-dashed rounded"
                     style={{ 
                       left: `${left + width}px`, 
                       width: `${floatWidth}px` 
                     }}
                   />
                  )}

                  {/* Main Task Bar */}
                  <div
                    className={`absolute top-1 h-6 rounded px-2 flex items-center text-[10px] text-white overflow-hidden whitespace-nowrap shadow-sm transition-all
                      ${isPlaceholder 
                        ? 'bg-transparent border-2 border-dashed border-slate-500 text-slate-400' 
                        : isCritical 
                          ? 'bg-gradient-to-r from-red-600 to-red-500 border border-red-400 shadow-[0_0_10px_rgba(239,68,68,0.3)]' 
                          : 'bg-blue-600 border border-blue-500'
                      }
                    `}
                    style={{ left: `${left}px`, width: `${width}px` }}
                  >
                    {task.duration} x {task.unit}
                  </div>
                  
                  {/* Dependencies Lines (Simplified visualization) */}
                  {task.dependencies.length > 0 && (
                     <div className="absolute left-0 top-1/2 -translate-x-full pr-2 text-xs text-slate-600 flex items-center">
                        <LinkIcon className="w-3 h-3 mr-1" />
                     </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
